import { createFileRoute } from "@tanstack/react-router";
import { useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { requireAuth } from "~/lib/auth-guard";
import { RouteErrorBoundary } from "~/components/RouteErrorBoundary";
import { orpc } from "~/lib/orpc-client";
import { AccessibleField } from "~/components/ui/accessible-field";
import { Button, Input } from "@alchema/ui";
import * as m from "~/paraglide/messages";

export const Route = createFileRoute("/feedback")({
  beforeLoad: async () => {
    await requireAuth();
  },
  component: FeedbackPage,
  errorComponent: RouteErrorBoundary,
});

type FeedbackCategory = "bug" | "feature" | "question" | "other";

function FeedbackPage() {
  const [category, setCategory] = useState<FeedbackCategory>("bug");
  const [subject, setSubject] = useState("");
  const [description, setDescription] = useState("");
  const [submitted, setSubmitted] = useState(false);

  const mutation = useMutation(orpc.feedback.submit.mutationOptions());

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    mutation.mutate(
      { category, subject, description },
      {
        onSuccess: () => {
          setSubmitted(true);
          setCategory("bug");
          setSubject("");
          setDescription("");
        },
      },
    );
  }

  return (
    <div className="mx-auto max-w-2xl p-6 sm:p-8">
      {/* Attio page header */}
      <div className="animate-fade-up">
        <h1
          className="font-display text-[22px] font-semibold tracking-tight"
          style={{ color: "var(--text)", fontFamily: "var(--font-display, inherit)" }}
        >
          {m.feedback_title()}
        </h1>
        <p
          className="mt-1 text-sm"
          style={{ color: "var(--text-muted)" }}
        >
          Report a bug, suggest a feature, or ask a question.
        </p>
      </div>

      {/* Inline success message */}
      {submitted && (
        <div
          className="mt-6 rounded-[10px] border px-4 py-3 text-sm"
          style={{
            borderColor: "var(--success, #22c55e)",
            backgroundColor: "var(--success-subtle, rgba(34,197,94,0.08))",
            color: "var(--success, #22c55e)",
          }}
          role="status"
        >
          {m.feedback_success()}
          <button
            type="button"
            className="ml-3 text-sm underline opacity-70 hover:opacity-100"
            onClick={() => setSubmitted(false)}
          >
            Send another
          </button>
        </div>
      )}

      <form onSubmit={handleSubmit} className="mt-8 space-y-5">
        <AccessibleField label={m.feedback_category()} required>
          {(props) => (
            <select
              {...props}
              value={category}
              onChange={(e) =>
                setCategory(e.target.value as FeedbackCategory)
              }
              className="w-full rounded-[10px] border px-3 py-2 text-sm outline-none transition-colors duration-300 focus:border-[var(--accent)]"
              style={{
                borderColor: "var(--border-subtle)",
                backgroundColor: "var(--surface-raised)",
                color: "var(--text)",
              }}
            >
              <option value="bug">{m.feedback_category_bug()}</option>
              <option value="feature">
                {m.feedback_category_feature()}
              </option>
              <option value="question">
                {m.feedback_category_question()}
              </option>
              <option value="other">{m.feedback_category_other()}</option>
            </select>
          )}
        </AccessibleField>

        <AccessibleField label={m.feedback_subject()} required>
          {(props) => (
            <Input
              {...props}
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              maxLength={200}
              required
            />
          )}
        </AccessibleField>

        <AccessibleField label={m.feedback_description()} required>
          {(props) => (
            <textarea
              {...props}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={5}
              maxLength={5000}
              required
              minLength={10}
              className="w-full rounded-[10px] border px-3 py-2 text-sm outline-none transition-colors duration-300 focus:border-[var(--accent)] resize-none"
              style={{
                borderColor: "var(--border-subtle)",
                backgroundColor: "var(--surface-raised)",
                color: "var(--text)",
              }}
            />
          )}
        </AccessibleField>

        {mutation.isError && (
          <p
            className="text-sm"
            style={{ color: "var(--destructive, #ef4444)" }}
            role="alert"
          >
            {m.feedback_error()}
          </p>
        )}

        <Button type="submit" disabled={mutation.isPending}>
          {mutation.isPending ? "..." : m.feedback_submit()}
        </Button>
      </form>
    </div>
  );
}
